/**
 * App Layout
 * Persistent chrome for the authenticated tenant application. Pages render
 * into the <Outlet /> and own only their content area.
 *
 * Structure:
 * - lg+ (>=1024px): fixed left sidebar (logo, grouped scrollable nav, footer
 *   with the signed-in user, Change password and Logout). Content is offset
 *   with lg:pl-64.
 * - <lg: slim sticky top bar (logo + hamburger). The hamburger opens the SAME
 *   sidebar element as an off-canvas drawer with a backdrop; it closes on
 *   backdrop click, Escape, the X button, or a route change.
 *
 * Global search (tracker item 61): Ctrl/Cmd-K anywhere, or the search button
 * at the top of the sidebar / in the mobile top bar, opens GlobalSearchPalette
 * (staff roles only, canUseGlobalSearch). Opening it closes the drawer.
 *
 * Staff notification bell (tracker item 62): ADMIN / OFFICE_MANAGER get a bell
 * with an unread badge in the mobile top bar and at the top of the sidebar (one
 * shared poller, see useStaffNotificationCount).
 *
 * The nav is rendered once in the DOM and made responsive with CSS only, so
 * tests that look links up by text never see duplicates. Nav items are
 * data-driven (NAV_GROUPS): adding one is a one-line change with its own
 * isVisible predicate. Collapsed groups persist in localStorage.
 *
 * Not used by the system-admin console, which has its own provider tree.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CHANGE_PASSWORD_PATH } from '../api/client';
import { User, UserRole } from '../api/types';
import { canUseGlobalSearch, canUseTechnicianView, canManageEstimates, canManageInventory, canManageRecurring, canManageRequests, canPrintWorkOrders, canReadCustomers, canViewDispatch, canViewLedger, canViewReports, canViewSurveys, canManageBundles } from '../utils/permissions';
import cinrmLogo from '../assets/cinrmlogo.png';
import { useInboxNav } from '../pages/inbox/useInboxNav';
import { GlobalSearchPalette, SearchIcon } from './GlobalSearchPalette';
import { StaffNotificationBell } from './StaffNotificationBell';
import { useStaffNotificationCount } from '../features/staff-notifications/useStaffNotificationCount';
import { globalSearchShortcutLabel, isGlobalSearchShortcut } from '../utils/globalSearch';

interface NavItem {
  to: string;
  label: string;
  /** Only match the exact path (needed for "/" so it is not active everywhere). */
  end?: boolean;
  /** Extra path prefix that should also mark this item active. */
  activePrefix?: string;
  /** Omit to show the item to every signed-in user. */
  isVisible?: (user: User | null) => boolean;
  /** The conversation inbox (tracker item 56): shown by the company's inboxAccess setting, with an unread badge */
  inbox?: true;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const isOffice = (user: User | null) => user?.role === UserRole.ADMIN || user?.role === UserRole.OFFICE_MANAGER;

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Overview',
    items: [{ to: '/', label: 'Dashboard', end: true }],
  },
  {
    label: 'Customers',
    items: [
      { to: '/customers', label: 'Customers', isVisible: canReadCustomers },
      // Conversations with customers (tracker item 56): ADMIN / OFFICE_MANAGER, technicians when inboxAccess is EVERYONE
      { to: '/inbox', label: 'Inbox', activePrefix: '/inbox', inbox: true },
      // Service, reschedule and portal access requests from the customer portal (tracker item 45; office roles only)
      { to: '/requests', label: 'Requests', activePrefix: '/requests', isVisible: canManageRequests },
      // Estimates / quotes (tracker item 39; office roles only)
      { to: '/estimates', label: 'Estimates', activePrefix: '/estimates', isVisible: canManageEstimates },
      // Customer satisfaction survey answers (tracker item 69; office roles only)
      { to: '/surveys', label: 'Surveys', activePrefix: '/surveys', isVisible: canViewSurveys },
      // Service bundle renewals (tracker item 85; office roles only)
      { to: '/renewals', label: 'Renewals', activePrefix: '/renewals', isVisible: canManageBundles },
    ],
  },
  {
    label: 'Work',
    items: [
      { to: '/jobs', label: 'Jobs', activePrefix: '/jobs', isVisible: (u) => isOffice(u) || u?.role === UserRole.TECHNICIAN },
      // Mobile technician view (tracker item 42); staff roles
      { to: '/tech', label: 'Technician view', activePrefix: '/tech', isVisible: canUseTechnicianView },
      { to: '/dispatch', label: 'Dispatch', activePrefix: '/dispatch', isVisible: canViewDispatch },
      // Bulk work order printing (tracker item 58; office roles only)
      { to: '/work-orders', label: 'Work Orders', activePrefix: '/work-orders', isVisible: canPrintWorkOrders },
      // Office roles only (TECHNICIAN can still open /recurring read-only)
      { to: '/recurring', label: 'Recurring', activePrefix: '/recurring', isVisible: canManageRecurring },
    ],
  },
  {
    label: 'Billing',
    items: [
      { to: '/invoices', label: 'Invoices', activePrefix: '/invoices', isVisible: isOffice },
      { to: '/payments', label: 'Payments', activePrefix: '/payments', isVisible: isOffice },
    ],
  },
  {
    label: 'Accounting',
    items: [
      { to: '/ledger', label: 'Ledger', activePrefix: '/ledger', isVisible: canViewLedger },
      { to: '/reports', label: 'Reports', activePrefix: '/reports', isVisible: canViewReports },
    ],
  },
  {
    label: 'Inventory',
    items: [{ to: '/inventory', label: 'Inventory', activePrefix: '/inventory', isVisible: canManageInventory }],
  },
  {
    label: 'Admin',
    // /settings shows a card index (SettingsIndexPage); the whole /settings
    // section highlights this item.
    items: [{ to: '/settings', label: 'Settings', activePrefix: '/settings' }],
  },
];

const COLLAPSED_STORAGE_KEY = 'crm.nav.collapsed';
const SIDEBAR_ID = 'app-sidebar';

const navLinkBase = 'flex items-center min-h-[44px] lg:min-h-[36px] px-3 py-2 rounded-md text-sm font-normal transition-colors';
const navLinkActive = 'bg-blue-50 text-blue-700 font-medium';
const navLinkIdle = 'text-gray-600 hover:text-gray-900 hover:bg-gray-100';

/** Mirrors NavLink's own matching, plus activePrefix; used to keep the active group expanded. */
function isItemActive(item: NavItem, pathname: string): boolean {
  if (item.activePrefix !== undefined && pathname.startsWith(item.activePrefix)) return true;
  if (pathname === item.to) return true;
  return !item.end && pathname.startsWith(item.to.endsWith('/') ? item.to : `${item.to}/`);
}

/** Storage may be unavailable or throw (private mode, blocked site data); fall back to all expanded. */
function loadCollapsed(): string[] {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(COLLAPSED_STORAGE_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function saveCollapsed(labels: string[]) {
  try {
    window.localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify(labels));
  } catch {
    // Non-essential preference; ignore storage failures.
  }
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="currentColor"
      className={`h-4 w-4 transition-transform ${expanded ? 'rotate-0' : '-rotate-90'}`}
    >
      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
    </svg>
  );
}

export function AppLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<string[]>(loadCollapsed);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const canSearch = canUseGlobalSearch(user);
  const shortcutLabel = globalSearchShortcutLabel();

  const openSearch = useCallback(() => {
    // Closing the drawer must not pull focus back to the hamburger: the palette takes it.
    wasOpen.current = false;
    setDrawerOpen(false);
    setSearchOpen(true);
  }, []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  // Ctrl/Cmd-K opens the global search from anywhere in the app.
  useEffect(() => {
    if (!canSearch) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isGlobalSearchShortcut(event)) return;
      event.preventDefault();
      openSearch();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [canSearch, openSearch]);

  // A route change (e.g. opening a result) closes it.
  useEffect(() => {
    setSearchOpen(false);
  }, [pathname]);

  // Close the drawer whenever the route changes (a nav link was tapped).
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  // While open: Escape closes, body scroll is locked, and focus moves into the
  // drawer; on close focus returns to the hamburger.
  useEffect(() => {
    if (!drawerOpen) {
      if (wasOpen.current) menuButtonRef.current?.focus();
      wasOpen.current = false;
      return undefined;
    }
    wasOpen.current = true;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    // Growing past lg turns the drawer into the static sidebar; drop the lock.
    const desktop = typeof window.matchMedia === 'function' ? window.matchMedia('(min-width: 1024px)') : null;
    const onDesktop = (event: MediaQueryListEvent) => {
      if (event.matches) setDrawerOpen(false);
    };
    desktop?.addEventListener?.('change', onDesktop);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      desktop?.removeEventListener?.('change', onDesktop);
    };
  }, [drawerOpen]);

  const toggleGroup = (label: string) => {
    setCollapsed((prev) => {
      const next = prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label];
      saveCollapsed(next);
      return next;
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  const inboxNav = useInboxNav(user ?? null, pathname);
  const staffBell = useStaffNotificationCount(user ?? null, pathname);

  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => (item.inbox ? inboxNav.visible : !item.isVisible || item.isVisible(user ?? null))),
  })).filter((group) => group.items.length > 0);

  const logo = (
    <Link to="/" aria-label="CinRM dashboard" className="shrink-0">
      <img src={cinrmLogo} alt="CinRM" className="w-36 h-auto" />
    </Link>
  );

  return (
    <div className="min-h-screen bg-gray-100 print:min-h-0 print:bg-white">
      {/* Mobile top bar */}
      <header className="lg:hidden print:hidden sticky top-0 z-20 bg-white shadow">
        <div className="px-4 py-2 flex items-center justify-between gap-3">
          {logo}
          <div className="flex items-center gap-1">
            {staffBell.visible && (
              <StaffNotificationBell unread={staffBell.unread} capped={staffBell.capped} onChanged={staffBell.refresh} placement="top-bar" />
            )}
            {canSearch && (
              <button
                type="button"
                aria-label="Open search"
                aria-haspopup="dialog"
                aria-keyshortcuts="Control+K Meta+K"
                onClick={openSearch}
                className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                <SearchIcon className="h-6 w-6" />
              </button>
            )}
            <button
              ref={menuButtonRef}
              type="button"
              aria-label="Open menu"
              aria-expanded={drawerOpen}
              aria-controls={SIDEBAR_ID}
              onClick={() => setDrawerOpen(true)}
              className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-6 w-6">
                <path strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Drawer backdrop (mobile only) */}
      {drawerOpen && (
        <div
          aria-hidden="true"
          data-testid="nav-backdrop"
          onClick={closeDrawer}
          className="lg:hidden print:hidden fixed inset-0 z-30 bg-gray-900/50"
        />
      )}

      {/* Sidebar: static at lg+, off-canvas drawer below lg */}
      <aside
        id={SIDEBAR_ID}
        className={`print:hidden fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 flex flex-col transition-[transform,visibility] duration-200 ease-in-out lg:visible lg:translate-x-0 ${
          drawerOpen ? 'visible translate-x-0' : 'invisible -translate-x-full'
        }`}
      >
        <div className="px-4 py-3 flex items-center justify-between gap-3 border-b border-gray-200">
          {logo}
          {staffBell.visible && (
            <div className="hidden lg:block">
              <StaffNotificationBell unread={staffBell.unread} capped={staffBell.capped} onChanged={staffBell.refresh} placement="sidebar" />
            </div>
          )}
          <button
            ref={closeButtonRef}
            type="button"
            aria-label="Close menu"
            onClick={closeDrawer}
            className="lg:hidden inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-6 w-6">
              <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        {canSearch && (
          <div className="px-3 pt-3">
            <button
              type="button"
              aria-label={`Search records (${shortcutLabel})`}
              aria-haspopup="dialog"
              aria-keyshortcuts="Control+K Meta+K"
              onClick={openSearch}
              className="w-full flex items-center gap-2 min-h-[44px] lg:min-h-[36px] px-3 rounded-md border border-gray-300 bg-white text-sm text-gray-500 hover:border-gray-400 hover:text-gray-700"
            >
              <SearchIcon className="h-4 w-4 shrink-0" />
              <span className="flex-1 text-left">Search…</span>
              <kbd className="hidden lg:inline rounded border border-gray-300 bg-gray-50 px-1.5 text-xs font-sans text-gray-500">{shortcutLabel}</kbd>
            </button>
          </div>
        )}

        <nav aria-label="Main" className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
          {visibleGroups.map((group) => {
            // The group holding the current route never hides it.
            const expanded = !collapsed.includes(group.label) || group.items.some((item) => isItemActive(item, pathname));
            const listId = `nav-group-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
            return (
              <div key={group.label}>
                <button
                  type="button"
                  aria-expanded={expanded}
                  aria-controls={listId}
                  onClick={() => toggleGroup(group.label)}
                  className="w-full flex items-center justify-between min-h-[44px] lg:min-h-[32px] px-3 rounded-md bg-gray-100 text-xs font-bold uppercase tracking-wider text-gray-900 hover:bg-gray-200"
                >
                  {group.label}
                  <ChevronIcon expanded={expanded} />
                </button>
                {expanded && (
                  <ul id={listId} className="mt-1 ml-4 pl-2 border-l-2 border-gray-200 space-y-1">
                    {group.items.map((item) => (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          end={item.end}
                          className={({ isActive }) => {
                            const active =
                              isActive ||
                              (item.activePrefix !== undefined && pathname.startsWith(item.activePrefix));
                            return `${navLinkBase} ${active ? navLinkActive : navLinkIdle}`;
                          }}
                        >
                          {item.label}
                          {item.inbox && inboxNav.unread > 0 && (
                            <span
                              data-testid="inbox-unread-badge"
                              aria-label={`${inboxNav.unread} unread`}
                              className="ml-auto inline-flex min-w-[1.5rem] justify-center rounded-full bg-blue-600 px-2 text-xs font-semibold text-white"
                            >
                              {inboxNav.unread > 99 ? '99+' : inboxNav.unread}
                            </span>
                          )}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-gray-200 px-4 py-3 space-y-2">
          {user?.email && (
            <p className="text-sm text-gray-600 truncate" title={user.email}>
              Signed in as <strong>{user.email}</strong>
            </p>
          )}
          <Link
            to={CHANGE_PASSWORD_PATH}
            className="flex items-center min-h-[44px] lg:min-h-[32px] text-sm font-semibold text-blue-600 hover:text-blue-700"
          >
            Change password
          </Link>
          <Link
            to="/account/security"
            className="flex items-center min-h-[44px] lg:min-h-[32px] text-sm font-semibold text-blue-600 hover:text-blue-700"
          >
            Two-step verification
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            className="w-full min-h-[44px] lg:min-h-[36px] bg-red-600 hover:bg-red-700 text-white text-sm font-semibold py-2 px-4 rounded-md transition duration-200"
          >
            Logout
          </button>
        </div>
      </aside>

      {/* No app chrome on paper: pages that print in place (Work Orders) rely on this */}
      <div className="lg:pl-64 print:pl-0">
        <Outlet />
      </div>

      {canSearch && searchOpen && <GlobalSearchPalette onClose={closeSearch} />}
    </div>
  );
}
